import { X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { useJobBoardFilters, type JobBoardFilters } from '@/hooks/useJobBoards';

interface JobBoardFilterSidebarProps {
  filters: JobBoardFilters;
  onFiltersChange: (filters: JobBoardFilters) => void;
  isOpen?: boolean;
  onClose?: () => void;
}

export function JobBoardFilterSidebar({ 
  filters, 
  onFiltersChange, 
  isOpen = false, 
  onClose 
}: JobBoardFilterSidebarProps) {
  const { data: filterOptions } = useJobBoardFilters();

  const toggleCategory = (category: string) => {
    const newCategories = filters.categories.includes(category)
      ? filters.categories.filter(c => c !== category)
      : [...filters.categories, category];
    
    onFiltersChange({ ...filters, categories: newCategories });
  };

  const toggleCountry = (country: string) => {
    const newCountries = filters.countries.includes(country)
      ? filters.countries.filter(c => c !== country)
      : [...filters.countries, country];
    
    onFiltersChange({ ...filters, countries: newCountries });
  };

  const toggleJobType = (jobType: string) => {
    const currentJobTypes = filters.jobTypes || [];
    const newJobTypes = currentJobTypes.includes(jobType)
      ? currentJobTypes.filter(t => t !== jobType)
      : [...currentJobTypes, jobType];
    
    onFiltersChange({ ...filters, jobTypes: newJobTypes });
  };

  const toggleNiche = (niche: string) => {
    const currentNiches = filters.niches || [];
    const newNiches = currentNiches.includes(niche)
      ? currentNiches.filter(n => n !== niche)
      : [...currentNiches, niche];
    
    onFiltersChange({ ...filters, niches: newNiches });
  };

  const toggleWorkMode = (workMode: string) => {
    const currentWorkModes = filters.workModes || [];
    const newWorkModes = currentWorkModes.includes(workMode)
      ? currentWorkModes.filter(w => w !== workMode)
      : [...currentWorkModes, workMode];
    
    onFiltersChange({ ...filters, workModes: newWorkModes });
  };

  const toggleGeography = (geography: string) => {
    const currentGeographies = filters.geographies || [];
    const newGeographies = currentGeographies.includes(geography)
      ? currentGeographies.filter(g => g !== geography)
      : [...currentGeographies, geography];
    
    onFiltersChange({ ...filters, geographies: newGeographies });
  };

  const toggleRemoteOnly = (checked: boolean) => {
    onFiltersChange({ 
      ...filters, 
      remoteOnly: checked ? true : null 
    });
  };

  const clearAllFilters = () => {
    onFiltersChange({
      categories: [],
      countries: [],
      remoteOnly: null,
      jobTypes: [],
      niches: [],
      workModes: [],
      geographies: [],
    });
  };

  const activeFilterCount = 
    (filters.categories?.length || 0) + 
    (filters.countries?.length || 0) + 
    (filters.jobTypes?.length || 0) +
    (filters.niches?.length || 0) +
    (filters.workModes?.length || 0) +
    (filters.geographies?.length || 0) +
    (filters.remoteOnly !== null ? 1 : 0);

  const SidebarContent = () => (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between p-6 border-b">
        <div>
          <h2 className="text-lg font-semibold">Filters</h2>
          {activeFilterCount > 0 && (
            <p className="text-sm text-muted-foreground">
              {activeFilterCount} filter{activeFilterCount !== 1 ? 's' : ''} active
            </p>
          )}
        </div>
        {activeFilterCount > 0 && (
          <Button variant="ghost" size="sm" onClick={clearAllFilters}>
            Clear all
          </Button>
        )}
      </div>

      <ScrollArea className="flex-1 p-6">
        <div className="space-y-6">
          {/* Job Type Filter */}
          {filterOptions?.jobTypes && filterOptions.jobTypes.length > 0 && (
            <div>
              <h3 className="font-medium text-sm mb-3 flex items-center justify-between">
                Job Type
                {(filters.jobTypes?.length || 0) > 0 && (
                  <Badge variant="secondary" className="text-xs">
                    {filters.jobTypes?.length || 0}
                  </Badge>
                )}
              </h3>
              <div className="space-y-2">
                {filterOptions.jobTypes.map(jobType => (
                  <div key={jobType} className="flex items-center space-x-2">
                    <Checkbox
                      id={`jobtype-${jobType}`}
                      checked={filters.jobTypes?.includes(jobType) || false}
                      onCheckedChange={() => toggleJobType(jobType)}
                    />
                    <label
                      htmlFor={`jobtype-${jobType}`}
                      className="text-sm cursor-pointer flex-1 capitalize"
                    >
                      {jobType.replace('_', ' ')}
                    </label>
                  </div>
                ))}
              </div>
            </div>
          )}

          {filterOptions?.jobTypes && filterOptions.jobTypes.length > 0 && <Separator />}

          {/* Niche Filter */}
          {filterOptions?.niches && filterOptions.niches.length > 0 && (
            <div>
              <h3 className="font-medium text-sm mb-3 flex items-center justify-between">
                Niche
                {(filters.niches?.length || 0) > 0 && (
                  <Badge variant="secondary" className="text-xs">
                    {filters.niches?.length || 0}
                  </Badge>
                )}
              </h3>
              <div className="space-y-2">
                {filterOptions.niches.map(niche => (
                  <div key={niche} className="flex items-center space-x-2">
                    <Checkbox
                      id={`niche-${niche}`}
                      checked={filters.niches?.includes(niche) || false}
                      onCheckedChange={() => toggleNiche(niche)}
                    />
                    <label
                      htmlFor={`niche-${niche}`}
                      className="text-sm cursor-pointer flex-1 capitalize"
                    >
                      {niche}
                    </label>
                  </div>
                ))}
              </div>
            </div>
          )}

          {filterOptions?.niches && filterOptions.niches.length > 0 && <Separator />}

          {/* Work Mode Filter */}
          {filterOptions?.workModes && filterOptions.workModes.length > 0 && (
            <div>
              <h3 className="font-medium text-sm mb-3 flex items-center justify-between">
                Work Mode
                {(filters.workModes?.length || 0) > 0 && (
                  <Badge variant="secondary" className="text-xs">
                    {filters.workModes?.length || 0}
                  </Badge>
                )}
              </h3>
              <div className="space-y-2">
                {filterOptions.workModes.map(workMode => (
                  <div key={workMode} className="flex items-center space-x-2">
                    <Checkbox
                      id={`workmode-${workMode}`}
                      checked={filters.workModes?.includes(workMode) || false}
                      onCheckedChange={() => toggleWorkMode(workMode)}
                    />
                    <label
                      htmlFor={`workmode-${workMode}`}
                      className="text-sm cursor-pointer flex-1 capitalize"
                    >
                      {workMode}
                    </label>
                  </div>
                ))}
              </div>
            </div>
          )}

          {filterOptions?.workModes && filterOptions.workModes.length > 0 && <Separator />}

          {/* Geography Filter */}
          {filterOptions?.geographies && filterOptions.geographies.length > 0 && (
            <div>
              <h3 className="font-medium text-sm mb-3 flex items-center justify-between">
                Geography
                {(filters.geographies?.length || 0) > 0 && (
                  <Badge variant="secondary" className="text-xs">
                    {filters.geographies?.length || 0}
                  </Badge>
                )}
              </h3>
              <div className="space-y-2">
                {filterOptions.geographies.map(geography => (
                  <div key={geography} className="flex items-center space-x-2">
                    <Checkbox
                      id={`geography-${geography}`}
                      checked={filters.geographies?.includes(geography) || false}
                      onCheckedChange={() => toggleGeography(geography)}
                    />
                    <label
                      htmlFor={`geography-${geography}`}
                      className="text-sm cursor-pointer flex-1 capitalize"
                    >
                      {geography}
                    </label>
                  </div>
                ))}
              </div>
            </div>
          )}

          {filterOptions?.geographies && filterOptions.geographies.length > 0 && <Separator />}

          {/* Legacy Filters - keeping for backward compatibility */}
          {/* Category Filter */}
          <div>
            <h3 className="font-medium text-sm mb-3 flex items-center justify-between">
              Category
              {(filters.categories?.length || 0) > 0 && (
                <Badge variant="secondary" className="text-xs">
                  {filters.categories?.length || 0}
                </Badge>
              )}
            </h3>
            <div className="space-y-2">
              {filterOptions?.categories.map(category => {
                const label = category === 'design_niche' ? 'Design Niche' : 'General';
                return (
                  <div key={category} className="flex items-center space-x-2">
                    <Checkbox
                      id={`category-${category}`}
                      checked={filters.categories?.includes(category) || false}
                      onCheckedChange={() => toggleCategory(category)}
                    />
                    <label
                      htmlFor={`category-${category}`}
                      className="text-sm cursor-pointer flex-1"
                    >
                      {label}
                    </label>
                  </div>
                );
              })}
            </div>
          </div>

          <Separator />

          {/* Remote Work Filter */}
          <div>
            <h3 className="font-medium text-sm mb-3">Legacy Remote Filter</h3>
            <div className="space-y-2">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="remote-focus"
                  checked={filters.remoteOnly === true}
                  onCheckedChange={toggleRemoteOnly}
                />
                <label htmlFor="remote-focus" className="text-sm cursor-pointer flex-1">
                  Remote-focused boards
                </label>
              </div>
            </div>
          </div>

          <Separator />

          {/* Country/Region Filter */}
          {filterOptions?.countries && filterOptions.countries.length > 0 && (
            <div>
              <h3 className="font-medium text-sm mb-3 flex items-center justify-between">
                Legacy Region/Country
                {(filters.countries?.length || 0) > 0 && (
                  <Badge variant="secondary" className="text-xs">
                    {filters.countries?.length || 0}
                  </Badge>
                )}
              </h3>
              <div className="space-y-2">
                {filterOptions.countries.slice(0, 10).map(country => (
                  <div key={country} className="flex items-center space-x-2">
                    <Checkbox
                      id={`country-${country}`}
                      checked={filters.countries?.includes(country) || false}
                      onCheckedChange={() => toggleCountry(country)}
                    />
                    <label
                      htmlFor={`country-${country}`}
                      className="text-sm cursor-pointer flex-1"
                    >
                      {country}
                    </label>
                  </div>
                ))}
                {filterOptions.countries.length > 10 && (
                  <p className="text-xs text-muted-foreground">
                    +{filterOptions.countries.length - 10} more regions available
                  </p>
                )}
              </div>
            </div>
          )}
        </div>
      </ScrollArea>
    </div>
  );

  // Render both mobile and desktop versions conditionally
  return (
    <>
      {/* Mobile Sheet */}
      {isOpen !== undefined && onClose && (
        <Sheet open={isOpen} onOpenChange={onClose}>
          <SheetContent side="left" className="p-0 w-80">
            <SheetHeader className="sr-only">
              <SheetTitle>Job Board Filters</SheetTitle>
              <SheetDescription>
                Filter job boards by category, region, and work type
              </SheetDescription>
            </SheetHeader>
            <SidebarContent />
          </SheetContent>
        </Sheet>
      )}

      {/* Desktop Sidebar */}
      <div className="hidden lg:block w-64 border-r border-border bg-background">
        <SidebarContent />
      </div>
    </>
  );
}